import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import SwitchToggle from "../../components/SwitchToggle";
import {
  useDeleteRoleMutation,
  useLazyGetRoleQuery,
  useUpdateRoleMutation,
} from "../../service/accessRole";
import { showError, showToast } from "../../constants/toasts";
import moment from "moment";
import ConfirmationDialog from "../../Modals/confirmationModal";
import Pagination from "../../components/Panigation";
import { AccessRoleData } from "../../service/accessRole";

const RolesList = ({ search }: { search?: string }) => {
  const navigate = useNavigate();

  const [getRolesApi, { isLoading }] = useLazyGetRoleQuery();
  const [updateRole] = useUpdateRoleMutation();
  const [deleteRole, { isLoading: isDeleting }] = useDeleteRoleMutation();

  const [rows, setRows] = useState<AccessRoleData[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [limit] = useState<number>(10);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<AccessRoleData | null>(null);

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setSelectedRole(null);
  };

  const getRoles = async (p?: number) => {
    try {
      const response = await getRolesApi({
        page: p || page,
        limit,
        ...(search && { search }),
      }).unwrap();
      const ok =
        response?.code === 200 ||
        response?.statusCode === 200 ||
        response?.status === 200;
      if (ok) {
        // some APIs wrap list in data.list; accessRole seems to return array directly or in data
        const list = response?.data?.list || response?.data || [];
        const count = response?.data?.totalCount || list?.length || 0;
        setRows(list as AccessRoleData[]);
        setTotalCount(count);
      } else {
        setRows([]);
        setTotalCount(0);
      }
    } catch (error: any) {
      showError(error?.data?.message || "Failed to fetch roles");
      setRows([]);
      setTotalCount(0);
    }
  };

  useEffect(() => {
    getRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleStatusChange = async (role: AccessRoleData) => {
    try {
      const res = await updateRole({
        id: role._id,
        body: { isBlocked: !role.isBlocked },
      }).unwrap();
      const ok =
        res?.code === 200 || res?.statusCode === 200 || res?.status === 200;
      if (ok) {
        showToast(
          `Role ${!role.isBlocked ? "blocked" : "activated"} successfully`
        );
        setRows((prev) =>
          prev.map((r) =>
            r._id === role._id ? { ...r, isBlocked: !role.isBlocked } : r
          )
        );
      } else {
        showError(res?.message || "Failed to update role status");
      }
    } catch (error: any) {
      showError(error?.data?.message || "Failed to update role status");
    }
  };

  const handleOpenDeleteModal = (role: AccessRoleData) => {
    setSelectedRole(role);
    setDeleteModalOpen(true);
  };

  const handleDeleteRole = async () => {
    if (!selectedRole) return;
    try {
      const res = await deleteRole({ id: selectedRole._id }).unwrap();
      const ok =
        res?.code === 200 || res?.statusCode === 200 || res?.status === 200;
      if (ok) {
        showToast("Role deleted successfully");
        handleCloseDeleteModal();
        if (rows.length === 1 && page > 1) {
          setPage((prev) => prev - 1);
          getRoles(page - 1);
        } else {
          getRoles(page);
        }
      } else {
        showError(res?.message || "Failed to delete role");
      }
    } catch (error: any) {
      showError(error?.data?.message || "Failed to delete role");
    }
  };

  return (
    <TableContainer className="table_container">
      <Table>
        <TableHead>
          <TableRow hover>
            <TableCell>Role Name</TableCell>
            <TableCell>Created Date</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows?.length > 0 ? (
            rows?.map((row) => (
              <TableRow hover key={row?._id}>
                <TableCell>
                  <a
                    href="/manage-subadmin/roles/detail"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate("/manage-subadmin/roles/detail", {
                        state: { role: row },
                      });
                    }}
                    className="user_block click_action"
                  >
                    <p>
                      <b>{row?.name || "-"}</b>
                    </p>
                  </a>
                </TableCell>
                <TableCell>
                  {row?.createdAt ? moment(row.createdAt).format("lll") : "-"}
                </TableCell>
                <TableCell>
                  <SwitchToggle
                    onChange={() => handleStatusChange(row)}
                    checked={!row?.isBlocked}
                  />
                </TableCell>
                <TableCell>
                  <div className="table_actions v2">
                    <Box>
                      <IconButton
                        onClick={() =>
                          navigate(`/manage-subadmin/roles/detail/${row?._id}`)
                        }
                      >
                        <img src="/static/images/eye_icon.svg" alt="view" />
                      </IconButton>
                    </Box>
                    <Box>
                      <IconButton
                        onClick={() =>
                          navigate(`/manage-subadmin/roles/add?id=${row?._id}`)
                        }
                      >
                        <img src="/static/images/edit2_icon.svg" alt="edit" />
                      </IconButton>
                    </Box>
                    <Box>
                      <IconButton onClick={() => handleOpenDeleteModal(row)}>
                        <img
                          src="/static/images/trash2_icon.svg"
                          alt="delete"
                        />
                      </IconButton>
                    </Box>
                  </div>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow hover>
              <TableCell colSpan={4}>
                <Box textAlign={"center"}>No data found</Box>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {totalCount > 0 && (
        <Pagination
          module={rows}
          page={page}
          onPageChange={(newPage: number) => {
            setPage(newPage);
            getRoles(newPage);
          }}
          totalPages={Math.ceil(totalCount / limit)}
          setPage={setPage}
          totalCount={totalCount}
        />
      )}
      <ConfirmationDialog
        open={deleteModalOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleDeleteRole}
        title="Delete Role"
        confirmText="Delete"
        cancelText="Cancel"
        message={`Are you sure you want to delete role "${selectedRole?.name || ""}"?`}
        isLoading={isDeleting}
      />
    </TableContainer>
  );
};

export default RolesList;
